import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Hammer,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  TreeDeciduous,
  Home,
  Gift,
  Church,
  Award,
  Ruler,
  Palette
} from 'lucide-react';
import { toast } from 'sonner';

// Wood types
const WOOD_TYPES = [
  { value: 'tiglio', label: 'Legno di tiglio', icon: TreeDeciduous },
  { value: 'cirmolo', label: 'Legno di cirmolo', icon: TreeDeciduous },
  { value: 'noce', label: 'Legno di noce', icon: TreeDeciduous },
  { value: 'ciliegio', label: 'Legno di ciliegio', icon: TreeDeciduous },
  { value: 'ulivo', label: 'Legno di ulivo', icon: TreeDeciduous },
  { value: 'castagno', label: 'Legno di castagno', icon: TreeDeciduous },
  { value: 'rovere', label: 'Legno di rovere', icon: TreeDeciduous },
  { value: 'ebano', label: 'Ebano', icon: TreeDeciduous },
  { value: 'radica', label: 'Radica (vari)', icon: Sparkles },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Sculpture types
const SCULPTURE_TYPES = [
  { value: 'religiosa', label: 'Scultura religiosa / Sacra', icon: Church },
  { value: 'figura', label: 'Figura umana / Ritratto', icon: Heart },
  { value: 'animale', label: 'Animale', icon: TreeDeciduous },
  { value: 'astratta', label: 'Scultura astratta', icon: Sparkles },
  { value: 'bassorilievo', label: 'Bassorilievo / Altorilievo', icon: Palette },
  { value: 'intarsio', label: 'Intarsio / Marqueterie', icon: Sparkles },
  { value: 'stemma', label: 'Stemma / Blasone', icon: Award },
  { value: 'pannello', label: 'Pannello decorativo', icon: Home },
  { value: 'mobile', label: 'Elemento per mobile', icon: Home },
  { value: 'altro', label: 'Altro', icon: Hammer },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'miniatura', label: 'Miniatura (fino a 15 cm)', icon: Ruler },
  { value: 'piccola', label: 'Piccola (15-30 cm)', icon: Ruler },
  { value: 'media', label: 'Media (30-60 cm)', icon: Ruler },
  { value: 'grande', label: 'Grande (60-100 cm)', icon: Ruler },
  { value: 'monumentale', label: 'Monumentale (oltre 100 cm)', icon: Ruler },
  { value: 'personalizzata', label: 'Misure personalizzate', icon: Sparkles },
];

// Finish options
const FINISH_OPTIONS = [
  { value: 'naturale', label: 'Finitura naturale (olio/cera)', icon: TreeDeciduous },
  { value: 'lucida', label: 'Lucidatura a specchio', icon: Star },
  { value: 'patinata', label: 'Patinata / Antichizzata', icon: Sparkles },
  { value: 'policroma', label: 'Policroma (dipinta)', icon: Palette },
  { value: 'dorata', label: 'Doratura / Foglia oro', icon: Star },
  { value: 'grezza', label: 'Grezza / Solo sgorbiata', icon: TreeDeciduous },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Purpose/Occasion
const PURPOSE_OPTIONS = [
  { value: 'arredo', label: 'Arredo casa / Decorazione', icon: Home },
  { value: 'religioso', label: 'Uso religioso / Chiesa', icon: Church },
  { value: 'regalo', label: 'Come regalo', icon: Gift },
  { value: 'commemorativo', label: 'Commemorativo / Ricordo', icon: Heart },
  { value: 'collezione', label: 'Per collezione', icon: Star },
  { value: 'restauro', label: 'Restauro / Integrazione', icon: Hammer },
  { value: 'professionale', label: 'Per ristorante / Attività', icon: Award },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '100-300', label: '€100 - €300', description: 'Piccola scultura' },
  { value: '300-600', label: '€300 - €600', description: 'Media complessità' },
  { value: '600-1200', label: '€600 - €1.200', description: 'Dettaglio elevato' },
  { value: '1200-2500', label: '€1.200 - €2.500', description: 'Opera importante' },
  { value: '2500-5000', label: '€2.500 - €5.000', description: 'Capolavoro' },
  { value: '5000+', label: '€5.000+', description: 'Monumentale / Complesso' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '2weeks', label: '2 Settimane', description: 'Piccola scultura' },
  { value: '1month', label: '1 Mese', description: 'Standard' },
  { value: '2months', label: '2 Mesi', description: 'Opera dettagliata' },
  { value: '3months', label: '3 Mesi', description: 'Grande formato' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example sculptures
const SCULPTURE_EXAMPLES = [
  {
    idea: "Madonna con bambino per cappella privata",
    result: "Scultura in tiglio 70cm, intaglio tradizionale tirolese, policromia con foglia oro sui dettagli",
    image: "https://images.unsplash.com/photo-1577083552431-6e5fd01988a3?w=400&h=300&fit=crop",
    category: "Religiosa"
  },
  {
    idea: "Aquila per ingresso villa",
    result: "Aquila ad ali spiegate in noce nazionale, 90cm apertura alare, finitura naturale a cera",
    image: "https://images.unsplash.com/photo-1578632292335-df3abbb0d586?w=400&h=300&fit=crop",
    category: "Animale"
  },
  {
    idea: "Bassorilievo con scena di vendemmia",
    result: "Pannello 120x80cm in ciliegio, bassorilievo con figure e paesaggio, patinato antico",
    image: "https://images.unsplash.com/photo-1544531586-fde5298cdd40?w=400&h=300&fit=crop",
    category: "Bassorilievo"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho commissionato un crocifisso in cirmolo per la nostra chiesa parrocchiale. L'intagliatore ha creato un'opera che commuove tutti i fedeli. Una maestria incredibile.",
    author: "Don Giuseppe R.",
    location: "Bolzano"
  },
  {
    text: "Volevo una scultura del mio cane per ricordarlo. L'artigiano ha lavorato dalle foto e il risultato è commovente, sembra vivo. Un'opera d'arte che terrò per sempre.",
    author: "Marta B.",
    location: "Verona"
  },
  {
    text: "Gestisco un agriturismo e cercavo decorazioni autentiche. I bassorilievi con scene di campagna hanno trasformato la sala da pranzo in un luogo magico.",
    author: "Franco C.",
    location: "Siena"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Quale legno è migliore per le sculture?",
    answer: "Il tiglio è il legno per eccellenza: tenero, omogeo, perfetto per i dettagli fini. Il cirmolo è tradizionale nelle sculture tirolesi. Il noce è più duro ma con venature bellissime. Per sculture da esterno, il rovere o il castagno sono più resistenti."
  },
  {
    question: "Posso commissionare una scultura da una foto?",
    answer: "Assolutamente sì! Molti clienti commissionano ritratti di persone, animali domestici o riproduzioni di opere. L'artigiano lavorerà dalle tue foto e ti invierà bozzetti prima di iniziare."
  },
  {
    question: "Quanto tempo ci vuole per una scultura?",
    answer: "Dipende dalla complessità: una piccola scultura richiede 2-4 settimane, una media 1-2 mesi, opere grandi o molto dettagliate possono richiedere 3-6 mesi. La policromia aggiunge tempo."
  },
  {
    question: "La scultura può essere dipinta (policroma)?",
    answer: "Sì, la tradizione italiana e tirolese prevede sculture policrome dipinte a mano. Si usano colori naturali e spesso foglia oro per i dettagli. Il risultato è di grande effetto."
  },
  {
    question: "È possibile restaurare sculture antiche in legno?",
    answer: "Certamente. I nostri scultori sono anche restauratori esperti. Possono riparare danni, reintegrare parti mancanti, pulire e rinnovare la policromia rispettando le tecniche originali."
  }
];

export default function ScultureIntagliRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    wood_type: '',
    sculpture_type: '',
    size: '',
    finish: '',
    purpose: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Sculture e Intagli Su Misura | Falegnameria Artigianale | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `sculpture-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi la scultura che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_sculpture_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const woodLabel = WOOD_TYPES.find(t => t.value === formData.wood_type)?.label || '';
      const sculptureLabel = SCULPTURE_TYPES.find(t => t.value === formData.sculpture_type)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(t => t.value === formData.size)?.label || '';
      const finishLabel = FINISH_OPTIONS.find(t => t.value === formData.finish)?.label || '';
      const purposeLabel = PURPOSE_OPTIONS.find(p => p.value === formData.purpose)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (sculptureLabel) details.push(`🪵  Tipo: ${sculptureLabel}`);
      if (woodLabel) details.push(`🌳  Legno: ${woodLabel}`);
      if (sizeLabel) details.push(`📐  Dimensioni: ${sizeLabel}`);
      if (finishLabel) details.push(`✨  Finitura: ${finishLabel}`);
      if (purposeLabel) details.push(`🎯  Utilizzo: ${purposeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${sculptureLabel} in ${woodLabel}`.trim() || 'Scultura su misura',
        description: fullDescription,
        category: 'Sculture e Intagli',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli scultori.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('sculpture-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Sculture e Intagli Su Misura | Falegnameria Artigianale Italiana | Rarifex</title>
        <meta name="description" content="Sculture e intagli in legno su misura realizzati da maestri scultori italiani. Tiglio, cirmolo, noce per arte sacra, figure, bassorilievi. Artigianato Made in Italy." />
        <meta name="keywords" content="scultura legno, intaglio legno, scultura su misura, arte sacra legno, bassorilievo, scultore legno, intagliatore, scultura tirolese" />
        <link rel="canonical" href="https://www.rarifex.com/sculture-intagli-su-misura" />
        <meta property="og:title" content="Sculture e Intagli Su Misura | Falegnameria Artigianale Italiana | Rarifex" />
        <meta property="og:description" content="Sculture e intagli in legno su misura realizzati da maestri scultori italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/sculture-intagli-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-amber-50/50 via-white to-stone-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Hammer className="w-4 h-4" />
                    Arte dell'Intaglio
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    La scultura perfetta{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-stone-600">
                      creata per te
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maestri scultori italiani danno forma ai tuoi sogni.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Figure sacre, ritratti, bassorilievi: ogni colpo di sgorbia racconta una storia.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Scultori verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="sculpture-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Madonna in legno per cappella"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="sculpture-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi la scultura che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Soggetto, stile, dimensioni, dettagli importanti. Allega foto di riferimento se ne hai."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="sculpture-description-textarea"
                        />
                      </div>
                      
                      {/* Sculpture Type & Wood Type */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tipo</Label>
                          <select
                            value={formData.sculpture_type}
                            onChange={(e) => handleInputChange('sculpture_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="sculpture-type-select"
                          >
                            <option value="">Seleziona tipo...</option>
                            {SCULPTURE_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Legno</Label>
                          <select
                            value={formData.wood_type}
                            onChange={(e) => handleInputChange('wood_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="sculpture-wood-select"
                          >
                            <option value="">Seleziona legno...</option>
                            {WOOD_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Size & Finish */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Dimensioni</Label>
                          <select
                            value={formData.size}
                            onChange={(e) => handleInputChange('size', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="sculpture-size-select"
                          >
                            <option value="">Seleziona...</option>
                            {SIZE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Finitura</Label>
                          <select
                            value={formData.finish}
                            onChange={(e) => handleInputChange('finish', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="sculpture-finish-select"
                          >
                            <option value="">Seleziona...</option>
                            {FINISH_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Purpose & Budget */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Utilizzo</Label>
                          <select
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="sculpture-purpose-select"
                          >
                            <option value="">Seleziona...</option>
                            {PURPOSE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="sculpture-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                          data-testid="sculpture-timeline-select"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* File Upload */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di riferimento (opzionale)
                        </Label>
                        <div className="mt-1.5">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors">
                            <Upload className="w-5 h-5 text-slate-400" />
                            <span className="text-sm text-slate-500">
                              {uploadingFile ? 'Caricamento...' : 'Carica immagini (max 5)'}
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleFileUpload}
                              className="hidden"
                              disabled={uploadingFile}
                            />
                          </label>
                        </div>
                        
                        {formData.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.attachments.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Riferimento ${index + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-amber-600 to-stone-600 hover:from-amber-700 hover:to-stone-700 text-white py-6 text-lg font-semibold shadow-lg"
                        data-testid="sculpture-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            Richiedi Preventivi Gratuiti
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-center text-slate-500">
                        Riceverai proposte da scultori verificati entro 24-48 ore
                      </p>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come Funziona
              </h2>
              <p className="text-lg text-slate-600">
                In 4 semplici passi avrai la tua scultura artigianale
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Raccontaci la scultura che desideri' },
                { icon: Users, title: 'Ricevi', desc: 'Proposte da scultori esperti' },
                { icon: Star, title: 'Scegli', desc: 'L\'artigiano e il progetto ideale' },
                { icon: Heart, title: 'Ammira', desc: 'La tua scultura prende vita' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-100 to-stone-100 flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 bg-gradient-to-b from-amber-50/30 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Sculture Realizzate
              </h2>
              <p className="text-lg text-slate-600">
                Dalla richiesta all'opera finita
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {SCULPTURE_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img
                    src={example.image}
                    alt={example.result}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5">
                    <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full mb-3">
                      {example.category}
                    </span>
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Richiesta:</strong> {example.idea}
                    </p>
                    <p className="text-sm text-slate-800">
                      <strong>Risultato:</strong> {example.result}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Dicono i Clienti
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                  <div className="text-sm">
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-slate-500">{testimonial.location}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gradient-to-b from-white to-amber-50/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
                Domande Frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index}
                    className="overflow-hidden cursor-pointer"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-5 flex justify-between items-center">
                      <h3 className="font-semibold text-slate-900">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 bg-gradient-to-r from-amber-600 to-stone-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto a commissionare la tua scultura?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Richiedi preventivi gratuiti dai migliori scultori italiani
            </p>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-amber-600 hover:bg-amber-50 font-semibold shadow-xl px-8 py-6 text-lg"
              data-testid="sculpture-final-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Inizia Ora - È Gratis
            </Button>
          </div>
        </section>
        
        {/* Auth Prompt Modal */}
        {showAuthPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Accedi per continuare
              </h3>
              <p className="text-slate-600 mb-6">
                Per inviare la tua richiesta e ricevere proposte dagli scultori, accedi o crea un account.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate('/login?redirect=/sculture-intagli-su-misura')}
                >
                  Accedi
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
